import AppPopup from '@/components/AppPopup.vue';
import {initSentry} from '@/library/sentry';
import {uniqueId} from '@/library/string';
import store from '@/store/index';
import * as Sentry from '@sentry/browser';
import axios, {AxiosError, type AxiosPromise} from 'axios';
import {type ActionTree} from 'vuex';
import {exportEnabled} from '../library/export';
import TopMessageHandler from '../library/topMessageHandler';
import {errorHandler, getLoginReferrer, XHR_STATUS_DEFAULT, XHR_STATUS_INVALID} from '../utils/portuna';

let favico: any = null;

let fetchPersonPromises = {
    user: {},
    role: {},
    employee: {},
    entity: {},
};

let stackFetchPersonPromises = {
    user: [],
    role: [],
    employee: [],
    entity: [],
};

let fetchTimeout = {
    user: 0,
    role: 0,
    employee: 0,
    entity: 0,
};

let tooltipUpdateInterval = 600 * 1000;

const actions: ActionTree<RootState, RootState> = {
    logout({}, router)
    {
        return axios.post('/logout').then((response) =>
        {
            if (response.data.status === 'SUCCESS')
            {
                let loginReferrer = getLoginReferrer(router.currentRoute);
                let resolved      = router.resolve(loginReferrer);

                // push only if full path are different
                if (resolved.fullPath !== router.currentRoute.value.fullPath)
                {
                    // reload the page to reset all stuff (vuex, xhr manager...)
                    window.location.pathname = resolved.fullPath;
                }
                else
                {
                    window.location.reload();
                }
            }
            else
            {
                throw new Error(response.data);
            }
        }).catch(errorHandler);
    },

    errorPage({commit, state}, payload)
    {
        commit('setError', payload);
    },

    clearError({commit})
    {
        commit('setError', null);
    },

    getPage({
        commit,
        dispatch,
    }, options: GetPageOptions)
    {
        const setLoading = 'undefined' === typeof options.setLoading || options.setLoading;

        setLoading && commit('setLoading', true);

        let request: AxiosPromise;
        if (options.data)
        {
            request = axios.post(options.url, options.data);
        }
        else
        {
            request = axios.get(options.url);
        }

        return new Promise((resolve, reject) =>
        {
            request
            .then(async (response) =>
            {
                if (200 === response.status && [XHR_STATUS_DEFAULT, XHR_STATUS_INVALID].includes(response.data.status))
                {
                    await resolve(response);
                }
                else
                {
                    reject(response);
                }
            })
            .catch((error) =>
            {
                if (error && 'object' === typeof error)
                {
                    error.handled = true;
                }

                reject(error);
                dispatch('errorPage', error);
            })
            .finally(() =>
            {
                setLoading && commit('setLoading', false);
            });
        });
    },

    loadAll({
        commit,
        dispatch,
    }, options: LoadAllOptions)
    {
        let promises = [];
        let errorPage = true;
        let setLoading = true;

        if(Array.isArray(options))
        {
            promises = options;
        }
        else
        {
            promises = options.promises;
            errorPage = 'undefined' === typeof options.errorPage ? true : options.errorPage;
            setLoading = 'undefined' === typeof options.setLoading ? true : options.setLoading;
        }

        setLoading && commit('setLoading', true);
        const all = Promise.all(promises);

        all.finally(() =>
        {
            setLoading && commit('setLoading', false);
        });

        if (errorPage)
        {
            all.catch(error => dispatch('errorPage', error));
        }

        return all;
    },

    setLayoutData({
        commit,
        dispatch,
    }, data)
    {
        data.loaded = Date.now();
        commit('setLayoutData', data);
    },

    getLayoutData({dispatch})
    {
        return axios.get('/ajax/get-layout-data').then((response) =>
        {
            try
            {
                let data = response.data.data;
                dispatch('setLayoutData', data);

                if (process.env.NODE_ENV === 'production')
                {
                    if (data.sentryActive)
                    {
                        initSentry();

                        const scope = Sentry.getCurrentScope();

                        if (data.sentry)
                        {
                            // set tags
                            if (typeof data.sentry.tags === 'object' && data.sentry.tags)
                            {
                                scope.setTags(data.sentry.tags);
                            }

                            // set context variables
                            if (typeof data.sentry.context === 'object' && data.sentry.context)
                            {
                                const context = data.sentry.context;
                                for (let key in context)
                                {
                                    if (context.hasOwnProperty(key))
                                    {
                                        scope.setContext(key, context[key]);
                                    }
                                }
                            }
                        }

                        if (data.user && data.user.id)
                        {
                            scope.setUser(Object.assign({
                                id: data.user.id,
                            }, data.sentry.user || {}));
                        }
                    }
                }

                return data;
            }
            catch (err)
            {
                throw new Error('NOT_LOGGED_IN');
            }
        });
    },

    fetchTileMenu({commit, state})
    {
        if (state.tileMenu !== null)
        {
            return Promise.resolve(state.tileMenu);
        }

        return axios.get('/ajax/load-tile-menu').then(({data: {data: tileMenu}}) =>
        {
            commit('setTileMenu', tileMenu);
        });
    },

    hideApp({commit, state}, hide)
    {
        if (state.hideApp !== hide)
        {
            commit('hideApp', hide);
        }
    },

    toggleFold({commit, state}, group)
    {
        commit(
            'updateFoldContainer',
            {
                group: group,
                open: !state.foldContainer[group],
            },
        );
    },

    setFold({commit, state}, {group, openState})
    {
        commit(
            'updateFoldContainer',
            {
                group,
                open: openState,
            },
        );
    },

    loadModuleMenu({dispatch, commit, state}, resource): Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            if ('undefined' === typeof resource)
            {
                commit('setModuleMenu', []);
                resolve();
            }
            else if (!state.moduleMenus[resource])
            {
                const onError = () =>
                {
                    commit('setModuleMenu', []);
                    TopMessageHandler.error('Die Navigation konnte nicht geladen werden.');
                    reject();
                };

                commit('setModuleMenu', []);

                axios.get('/ajax/load-module-menu/resource/' + resource).then(response =>
                {
                    if (typeof response.data.data.moduleMenu !== 'undefined')
                    {
                        commit(
                            'addModuleMenu',
                            {
                                resource,
                                menu: response.data.data.moduleMenu,
                            },
                        );

                        commit('setModuleMenu', state.moduleMenus[resource].menu);
                        resolve();
                    }
                    else
                    {
                        onError();
                    }
                })
                .catch(onError);
            }
            else
            {
                commit('setModuleMenu', state.moduleMenus[resource].menu);
                resolve();
            }
        });
    },

    resetModuleMenus({commit})
    {
        commit('resetModuleMenus');
    },

    clearModuleMenu({commit}, resource)
    {
        commit('clearModuleMenu', resource);
    },

    fetchTooltip({commit, state}, {id, type, url, data})
    {
        let params = [];

        if (data)
        {
            for (const key in data)
            {
                if (!data.hasOwnProperty(key))
                {
                    continue;
                }

                params.push(key, data[key]);
            }
        }

        const key = '' + id + (params.length ? '-' + params.join('-') : '');

        if (
            typeof state.tooltips[type] !== 'undefined' && state.tooltips[type][key]
            && Date.now() - state.tooltips[type][key].lastUpdate < tooltipUpdateInterval
        )
        {
            return Promise.resolve(state.tooltips[type][key].tooltip);
        }

        url = url || '/application/ajax/fetch-' + type + '-tooltip';

        return axios.get(url + '/id/' + id + (params.length ? '/' + params.join('/') : '')).then(({data: {tooltip}}) =>
        {
            commit(
                'tooltipSet',
                {
                    key,
                    type,
                    tooltip,
                },
            );

            return tooltip;
        });
    },

    reloadPerson({dispatch, commit, state}, {id, type})
    {
        dispatch('unsetPerson',
            {
                id: id,
                type: type,
            },
        )
        .then(() =>
        {
            dispatch('fetchPerson',
                {
                    id: id,
                    type: type,
                },
            );
        });
    },

    unsetPerson({dispatch, commit, state}, {id, type})
    {
        let stateKey = 'entity' === type ? 'entities' : type + 's';

        // force fetching of tooltip with current data
        commit(
            'tooltipDelete',
            {
                type,
                key: id,
            },
        );

        commit(
            'personSet',
            {
                type: stateKey,
                id: id,
                data: undefined,
            },
        );
    },

    fetchPerson({commit, state}, {id, type})
    {
        let stateKey = 'entity' === type ? 'entities' : type + 's';

        // if user already exists then return the user data directly
        if (typeof state[stateKey][id] !== 'undefined')
        {
            return state[stateKey][id];
        }
        // another xhr currently with this type and id is in progress -> return this promise
        else if (fetchPersonPromises[type][id])
        {
            return fetchPersonPromises[type][id].promise;
        }

        let promiseDeferrer = {} as { promise: Promise<unknown>, resolve: (arg: User) => void, reject: (arg: AxiosError) => void };
        promiseDeferrer.promise = new Promise((resolve, reject) =>
        {
            promiseDeferrer.resolve = resolve;
            promiseDeferrer.reject  = reject;
        });

        fetchPersonPromises[type][id] = promiseDeferrer;
        stackFetchPersonPromises[type].push(id);

        // small timeout to reduce xhr @ person lists
        fetchTimeout[type] && window.clearTimeout(fetchTimeout[type]);
        fetchTimeout[type] = window.setTimeout(() =>
        {
            const ids                      = stackFetchPersonPromises[type];
            fetchTimeout[type]             = 0;
            stackFetchPersonPromises[type] = [];

            const xhr = axios.post('/application/ajax/fetch-' + stateKey, {ids}).then(({data: {data}}) =>
            {
                for (let currentId of ids)
                {
                    if (!data.hasOwnProperty(currentId))
                    {
                        fetchPersonPromises[type][currentId].reject();
                        delete fetchPersonPromises[type][currentId];
                        continue;
                    }

                    commit(
                        'personSet',
                        {
                            type: stateKey,
                            id: currentId,
                            data: data[currentId],
                        },
                    );

                    fetchPersonPromises[type][currentId].resolve(data[currentId]);
                    delete fetchPersonPromises[type][currentId];
                }
                return data;
            });

            xhr.catch((error) =>
            {
                for (let currentId of ids)
                {
                    fetchPersonPromises[type][currentId].reject(error);
                    delete fetchPersonPromises[type][currentId];
                }
            });
        }, 5);

        return fetchPersonPromises[type][id].promise;
    },

    updateFaviconBadge({rootGetters})
    {
        try
        {
            if (!favico && !exportEnabled())
            {
                // @ts-ignore
                favico = new window.Favico();
            }

            if (favico)
            {
                const notificationCount = rootGetters['notification/unreadMessages'];
                const chatCount         = rootGetters['module/chat/totalUnreadMessages'];
                const totalCount        = notificationCount + chatCount;

                favico.badge(totalCount < 100 ? totalCount : '99+');
            }
        }
        catch (e)
        {
            // ignore error - prevent "Too many badges requests in queue"
        }
    },

    createPopup({
        commit,
        state,
    }, options): Promise<AppPopup>
    {
        const id = options.id ?? uniqueId();
        delete options.id;

        if (state.popups[id])
        {
            // reject if popup with id already exists
            return Promise.reject();
        }

        // Map events to eventHandler
        if (options.componentProps?.events)
        {
            for (const [key, func] of Object.entries(options.componentProps.events))
            {
                options.componentProps['on' + key.charAt(0).toUpperCase() + key.slice(1)] = func;
            }

            delete options.componentProps.events;
        }

        return new Promise((resolve, reject) =>
        {
            // Add callback that gets called on init of AppPopup to return popup
            options.registerPopupInstance = (instance: typeof AppPopup) =>
            {
                // Delete Popup from store if closed
                instance.emitter.on('close', () =>
                {
                    commit('deletePopup', id);
                });

                resolve(instance);
            };

            // Add Popup to Store (gets displayed via v-for)
            commit('addPopup',
                {
                    id,
                    popup: options,
                },
            );
        });
    },

    changePopup({commit}, options)
    {
        commit('changePopup', options);
    },
    subscribeToPopup({
        state,
        commit,
        dispatch,
    }, {
        id,
        eventName,
        callback,
        unsubscribeTime,
    })
    {
        // onClose is already a Property from AppPopup
        if (eventName === 'close')
        {
            eventName = 'closePopup';
        }
        
        if (state.popups[id])
        {
            const event = 'on' + eventName.charAt(0).toUpperCase() + eventName.slice(1);

            if (state.popups[id][event])
            {
                // if there is already a callback, change to array to retain old callbacks
                const callbackStack: (() => any)[] = typeof state.popups[id][event] === 'function' ? [state.popups[id][event]] : state.popups[id][event];

                commit('changePopup', {
                    id,
                    values: {
                        [event]: [...callbackStack, callback],
                    },
                });
            }
            else
            {
                commit('changePopup', {
                    id,
                    values: {
                        [event]: callback,
                    },
                });
            }
        }
        else
        {
            // If the popup you want to subscribe to don't exist, wait for its creation
            const unsubscribe = store.subscribe((mutation, state) =>
            {
                if (mutation.type === 'addPopup' && mutation.payload.id === id)
                {
                    dispatch('subscribeToPopup', {
                        id: id,
                        eventName: eventName,
                        callback: callback,
                    });

                    unsubscribe();
                }
            });

            if (unsubscribeTime)
            {
                setTimeout(unsubscribe, unsubscribeTime * 1000);
            }
        }
    },
};

export default actions;